import { ApolloError } from '@apollo/client'
import { ApolloQueryResult } from '@apollo/client/core/types'
import { bind, SUSPENSE } from '@react-rxjs/core'
import { combineLatest, filter, map, of, retry, tap, timer } from 'rxjs'
import { catchError, switchMap } from 'rxjs/operators'

import { TokenBaseCardsDocument, TokenBaseCardsQuery } from '../../graphql/magento'
import { isNotNull } from '../../utils/collectionTools'
import { logAndCaptureException } from '../../utils/errorTools'
import { fromObservableQuery } from '../../utils/rx/observable-query'
import { mapSuspended } from '../../utils/rx/operators'
import { latestAccessToken$ } from '../auth/state'
import { latestApolloClient$ } from '../graphql/apollo'

export type TokenBaseCard = NonNullable<NonNullable<TokenBaseCardsQuery['tokenBaseCards']>[number]>
export type SelectableTokenBaseCard = TokenBaseCard & {
  additional: {
    cc_exp_month: NonNullable<NonNullable<TokenBaseCard['additional']>['cc_exp_month']>
    cc_exp_year: NonNullable<NonNullable<TokenBaseCard['additional']>['cc_exp_year']>
    cc_last4: NonNullable<NonNullable<TokenBaseCard['additional']>['cc_last4']>
    cc_type: NonNullable<NonNullable<TokenBaseCard['additional']>['cc_type']>
  }
  address: NonNullable<TokenBaseCard['address']>
  hash: NonNullable<TokenBaseCard['hash']>
  label: NonNullable<TokenBaseCard['label']>
  last_use: NonNullable<TokenBaseCard['last_use']>
}

export const isSelectableTokenBaseCard = (
  card: NonNullable<TokenBaseCardsQuery['tokenBaseCards']>[number],
): card is SelectableTokenBaseCard =>
  !!card &&
  !!card.additional?.cc_exp_month &&
  !!card.additional?.cc_exp_year &&
  !!card.additional?.cc_last4 &&
  !!card.additional?.cc_type &&
  !!card.address &&
  !!card.hash &&
  !!card.label &&
  !!card.last_use

const normalizeExpirationDate = (card: SelectableTokenBaseCard): SelectableTokenBaseCard => ({
  ...card,
  additional: {
    ...card.additional,
    cc_exp_month: card.additional.cc_exp_month.padStart(2, '0'),
  },
})

export const [useSelectableTokenBaseCards, selectableTokenBaseCards$] = bind<
  | SUSPENSE
  | Omit<ApolloQueryResult<SelectableTokenBaseCard[] | undefined>, 'loading' | 'networkStatus'>
>(
  combineLatest([latestApolloClient$, latestAccessToken$.pipe(filter(isNotNull))]).pipe(
    switchMap(([client, token]) =>
      fromObservableQuery(
        client.watchQuery({
          query: TokenBaseCardsDocument,
          context: { token },
          errorPolicy: 'all',
          notifyOnNetworkStatusChange: true,
        }),
      ).pipe(
        retry({
          count: 3,
          delay: (_error, count) =>
            // retry with exponential backoff and max of 60 seconds
            timer(Math.min(60000, 2 ^ (count * 1000))),
        }),
      ),
    ),
    tap(({ errors }) => {
      if (errors?.length) {
        logAndCaptureException(new ApolloError({ graphQLErrors: errors }))
      }
    }),
    map(({ loading, networkStatus, ...rest }) => (loading ? SUSPENSE : rest)),
    mapSuspended(({ data, ...rest }) => ({
      ...rest,
      data: data?.tokenBaseCards?.filter(isSelectableTokenBaseCard)?.map(normalizeExpirationDate),
    })),
    retry({
      count: 3,
      delay: (_error, count) =>
        // retry with exponential backoff and max of 30 seconds
        timer(Math.min(30000, 2 ^ (count * 1000))),
    }),
    catchError((error: unknown) =>
      of({
        data: undefined,
        error:
          error instanceof ApolloError
            ? error
            : new ApolloError({
                networkError: error instanceof Error ? error : new Error(String(error)),
              }),
      }),
    ),
  ),
)
